import { useState, useRef, type FormEvent } from 'react';
import { aboutData } from '@/mocks/comics';
import Breadcrumb from '@/components/base/Breadcrumb';
import StructuredData from '@/components/base/StructuredData';

export default function AboutPage() {
  const { brandName, slogan, founded, description, mission, features, stats, contact } = aboutData;
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const aboutStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${brandName}`,
    description: `${brandName}${slogan}。${description}`,
    url: 'https://hanmanonline.com/about',
    inLanguage: 'zh-CN',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanonline.com' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pf03ub4qjhggaepst0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not json
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <main className="min-h-screen bg-background-100/70">
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        <section className="mb-16">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              关于{brandName}
            </h1>
            <p className="text-lg text-primary-500 font-medium mb-6">{slogan}</p>
            <p className="text-sm text-foreground-600 leading-relaxed">{description}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16">
            {stats.map((stat, i) => (
              <div
                key={i}
                className="text-center p-6 bg-background-50 rounded-lg border border-background-200"
              >
                <div className="text-2xl md:text-3xl font-bold text-accent-500 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  {stat.value}
                </div>
                <div className="text-xs text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8 mb-12">
            <h2 className="text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>品牌使命</h2>
            <p className="text-sm text-foreground-600 leading-relaxed">{mission}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-16">
            {features.map((feature, i) => (
              <div
                key={i}
                className="bg-background-50 rounded-lg border border-background-200 p-5 hover:border-primary-300 transition-all duration-300"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                  <i className={`${feature.icon} text-xl`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>联系方式</h2>
            <div className="bg-background-50 rounded-lg border border-background-200 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">{contact.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">微信公众号</p>
                  <p className="text-sm font-medium text-foreground-800">{contact.wechat}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                  <i className="ri-qq-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">QQ群</p>
                  <p className="text-sm font-medium text-foreground-800">{contact.qq}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">成立时间</p>
                  <p className="text-sm font-medium text-foreground-800">{founded}</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>发送消息</h2>
            <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="" className="bg-background-50 rounded-lg border border-background-200 p-6 space-y-4">
              {formStatus === 'success' && (
                <div className="p-3 rounded-md bg-accent-100 text-accent-700 text-sm">
                  消息已发送！感谢您的联系，我们会在24小时内回复。
                </div>
              )}
              {formStatus === 'error' && formError && (
                <div className="p-3 rounded-md bg-red-50 text-red-600 text-sm">
                  {formError}
                </div>
              )}

              <div>
                <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  您的姓名 <span className="text-red-400">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-3 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200"
                  placeholder="请输入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  电子邮箱 <span className="text-red-400">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-3 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200"
                  placeholder="请输入您的邮箱"
                />
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  className="w-full px-3 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200"
                  placeholder="消息主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  消息内容 <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200 resize-none"
                  placeholder="请输入您的消息，最多500个字符"
                ></textarea>
              </div>

              <input
                name="phone_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 pointer-events-none h-0 w-0"
                style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
              />

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 bg-primary-500 text-background-50 rounded-md text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? '发送中...' : '发送消息'}
              </button>
            </form>
          </div>
        </section>
      </div>
      <StructuredData data={aboutStructuredData} />
    </main>
  );
}